import axios from "axios";
import { io, Socket } from "socket.io-client";

const CHAT_BASE_URL =
  process.env.REACT_APP_CHAT_SERVICE_URL ||
  (process.env.REACT_APP_API_BASE_URL
    ? `${process.env.REACT_APP_API_BASE_URL}/api/v1/chat`
    : "https://devapi.salar.in/api/v1/chat");

function getSocketOrigin(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    return `${url.protocol}//${url.host}`;
  } catch {
    return rawUrl;
  }
}

const SOCKET_URL = getSocketOrigin(CHAT_BASE_URL);

export interface CustomerChatQuestionMeta {
  nodeId: string;
  answerType: string;
  uiComponent: string;
  text: string;
  placeholder?: string;
  nextNodeId?: string;
  options: { optionId: string; label: string; nextNodeId?: string }[];
  validation?: any;
}

export interface CustomerBotResponseData {
  conversationId: string;
  message: string;
  options: string[];
  currentState: string;
  question?: CustomerChatQuestionMeta;
  orderSummary?: {
    orderId: string;
    status: string;
    items?: { name: string; qty: number; price: number }[];
    totalAmount?: number;
    estimatedDelivery?: string;
  };
  ticketId?: string;
}

export interface CustomerChatMessageItem {
  _id?: string;
  conversationId?: string;
  sender: "user" | "bot" | "agent" | "system" | "note";
  senderName?: string;
  text: string;
  type?: "text" | "image" | "file" | "system";
  fileUrl?: string;
  attachments?: any[];
  timestamp?: string;
  createdAt?: string;
}

class CustomerChatService {
  private socket: Socket | null = null;
  private messageListeners: ((msg: CustomerChatMessageItem) => void)[] = [];
  private typingListeners: ((data: { sender: string; senderName?: string; isTyping: boolean }) => void)[] = [];
  private endListeners: ((data: any) => void)[] = [];

  private getAuthHeaders() {
    const token =
      typeof window !== "undefined"
        ? localStorage.getItem("token") || localStorage.getItem("customer_token") || localStorage.getItem("jwt")
        : "";
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // ─────────────────────────────────────────────────────────────
  // REST API Methods
  // ─────────────────────────────────────────────────────────────

  async startSession(orderId: string, serviceType: "ECOM" | "FOOD" | "QUICK" = "ECOM"): Promise<CustomerBotResponseData> {
    const endpoint = `${CHAT_BASE_URL}/chatbot/start`.replace(/([^:]\/)\/+/g, "$1");
    try {
      const res = await axios.post(
        endpoint,
        { orderId, serviceType },
        { headers: this.getAuthHeaders() }
      );
      return res.data;
    } catch (err: any) {
      console.warn("Direct chatbot/start failed, attempting fallback endpoint:", err.message);
      const fallbackUrl = `${SOCKET_URL}/api/chatbot/start`;
      const res = await axios.post(
        fallbackUrl,
        { orderId, serviceType },
        { headers: this.getAuthHeaders() }
      );
      return res.data;
    }
  }

  async submitAnswer(
    conversationId: string,
    dto: {
      nodeId: string;
      optionId?: string;
      optionIds?: string[];
      value?: string;
      fileUrls?: string[];
      text?: string;
    }
  ): Promise<CustomerBotResponseData> {
    const endpoint = `${CHAT_BASE_URL}/chatbot/${conversationId}/answer`.replace(/([^:]\/)\/+/g, "$1");
    try {
      const res = await axios.post(endpoint, dto, { headers: this.getAuthHeaders() });
      return res.data;
    } catch (err: any) {
      const fallbackUrl = `${SOCKET_URL}/api/chatbot/${conversationId}/answer`;
      const res = await axios.post(fallbackUrl, dto, { headers: this.getAuthHeaders() });
      return res.data;
    }
  }

  async sendMessage(conversationId: string, message: string): Promise<CustomerBotResponseData> {
    const endpoint = `${CHAT_BASE_URL}/chatbot/${conversationId}/message`.replace(/([^:]\/)\/+/g, "$1");
    try {
      const res = await axios.post(endpoint, { message }, { headers: this.getAuthHeaders() });
      return res.data;
    } catch (err: any) {
      const fallbackUrl = `${SOCKET_URL}/api/chatbot/${conversationId}/message`;
      const res = await axios.post(fallbackUrl, { message }, { headers: this.getAuthHeaders() });
      return res.data;
    }
  }

  async getMessages(conversationId: string): Promise<CustomerChatMessageItem[]> {
    const endpoint = `${CHAT_BASE_URL}/chatbot/${conversationId}/messages`.replace(/([^:]\/)\/+/g, "$1");
    try {
      const res = await axios.get(endpoint, { headers: this.getAuthHeaders() });
      return res.data || [];
    } catch {
      const fallbackUrl = `${SOCKET_URL}/api/chatbot/${conversationId}/messages`;
      const res = await axios.get(fallbackUrl, { headers: this.getAuthHeaders() });
      return res.data || [];
    }
  }

  async uploadImage(conversationId: string, file: File): Promise<{ fileUrl: string; message: string }> {
    const formData = new FormData();
    formData.append("image", file);
    const endpoint = `${CHAT_BASE_URL}/chatbot/${conversationId}/upload`.replace(/([^:]\/)\/+/g, "$1");
    const res = await axios.post(endpoint, formData, {
      headers: {
        ...this.getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  }

  // ─────────────────────────────────────────────────────────────
  // WebSocket Live Realtime Methods (Socket.IO)
  // ─────────────────────────────────────────────────────────────

  connectSocket(conversationId: string, customerName: string = "Customer") {
    if (this.socket) {
      this.socket.disconnect();
    }

    this.socket = io(SOCKET_URL, {
      query: { conversationId, role: "user" },
      transports: ["websocket", "polling"],
      reconnectionAttempts: 5,
    });

    this.socket.on("connect", () => {
      console.log(`[CustomerChatService] Socket connected (${this.socket?.id}) for conversation:`, conversationId);
      this.socket?.emit("join_conversation", {
        conversationId,
        agentName: customerName,
      });
    });

    this.socket.on("receive_message", (msg: CustomerChatMessageItem) => {
      this.messageListeners.forEach((fn) => fn(msg));
    });

    this.socket.on("typing", (data: { sender: string; senderName?: string; isTyping: boolean }) => {
      this.typingListeners.forEach((fn) => fn(data));
    });

    this.socket.on("conversation_ended", (data: any) => {
      this.endListeners.forEach((fn) => fn(data));
    });

    this.socket.on("disconnect", () => {
      console.log("[CustomerChatService] Socket disconnected");
    });
  }

  sendSocketMessage(conversationId: string, message: string, senderName: string = "Customer") {
    if (this.socket && this.socket.connected) {
      this.socket.emit("send_message", {
        conversationId,
        message,
        sender: "user",
        senderName,
      });
    }
  }

  sendTyping(conversationId: string, isTyping: boolean, userName: string = "Customer") {
    if (this.socket && this.socket.connected) {
      this.socket.emit("user_typing", {
        conversationId,
        isTyping,
        userName,
      });
    }
  }

  onMessage(cb: (msg: CustomerChatMessageItem) => void) {
    this.messageListeners.push(cb);
    return () => {
      this.messageListeners = this.messageListeners.filter((fn) => fn !== cb);
    };
  }

  onTyping(cb: (data: { sender: string; senderName?: string; isTyping: boolean }) => void) {
    this.typingListeners.push(cb);
    return () => {
      this.typingListeners = this.typingListeners.filter((fn) => fn !== cb);
    };
  }

  onEnded(cb: (data: any) => void) {
    this.endListeners.push(cb);
    return () => {
      this.endListeners = this.endListeners.filter((fn) => fn !== cb);
    };
  }

  disconnectSocket() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.messageListeners = [];
    this.typingListeners = [];
    this.endListeners = [];
  }
}

export const customerChatService = new CustomerChatService();
export default customerChatService;
